import { WordPosition, WordSearchGrid } from '../types/Level';
import { normalizeFinals } from './normalize';
import { HEBREW_LETTERS } from './keyLetters';

export const GRID_SIZE = 8;

type Direction = WordPosition['direction'];

// [row step, column step], matching the grid editor
const VECTORS: Record<Direction, [number, number]> = {
  'horizontal-reverse': [0, -1],
  'vertical': [1, 0],
  'vertical-up': [-1, 0],
  'diagonal-down-left': [1, -1],
  'diagonal-up-left': [-1, -1],
};

const ALL_DIRECTIONS = Object.keys(VECTORS) as Direction[];

// Same weighting the grid editor uses when it suggests a direction: mostly right-to-left, few diagonals
const DIRECTION_POOL: Direction[] = [
  'horizontal-reverse', 'horizontal-reverse', 'horizontal-reverse', 'horizontal-reverse',
  'vertical', 'vertical', 'vertical-up', 'vertical-up',
  'diagonal-down-left', 'diagonal-up-left',
];

// The letters a word occupies in the grid: no spaces or quotes, final letters converted.
export const gridLetters = (word: string): string => normalizeFinals(word.replace(/[\s'"]/g, ''));

interface Placement {
  startX: number;
  startY: number;
  direction: Direction;
}

const pick = <T>(items: T[], random: () => number): T => items[Math.floor(random() * items.length)];

const fits = (grid: string[][], letters: string, placement: Placement): boolean => {
  const [dRow, dCol] = VECTORS[placement.direction];
  for (let i = 0; i < letters.length; i++) {
    const row = placement.startX + dRow * i;
    const col = placement.startY + dCol * i;
    if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) return false;
    const cell = grid[row][col];
    if (cell && cell !== letters[i]) return false;
  }
  return true;
};

const placementsFor = (grid: string[][], letters: string, direction: Direction): Placement[] => {
  const options: Placement[] = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      const placement = { startX: row, startY: col, direction };
      if (fits(grid, letters, placement)) options.push(placement);
    }
  }
  return options;
};

const tryBuild = (words: string[], random: () => number): WordSearchGrid | null => {
  const grid: string[][] = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    grid.push(new Array<string>(GRID_SIZE).fill(''));
  }
  const wordPositions: WordPosition[] = [];

  // Longest first: they have the fewest places to go
  const ordered = [...words].sort((a, b) => gridLetters(b).length - gridLetters(a).length);

  for (const word of ordered) {
    const letters = gridLetters(word);
    if (letters.length === 0) return null;

    const preferred = pick(DIRECTION_POOL, random);
    const directions = [preferred, ...ALL_DIRECTIONS.filter(direction => direction !== preferred)];

    let chosen: Placement | null = null;
    for (const direction of directions) {
      const options = placementsFor(grid, letters, direction);
      if (options.length > 0) {
        chosen = pick(options, random);
        break;
      }
    }
    if (!chosen) return null;

    const [dRow, dCol] = VECTORS[chosen.direction];
    for (let i = 0; i < letters.length; i++) {
      grid[chosen.startX + dRow * i][chosen.startY + dCol * i] = letters[i];
    }
    wordPositions.push({ word, startX: chosen.startX, startY: chosen.startY, direction: chosen.direction });
  }

  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (!grid[row][col]) grid[row][col] = pick(HEBREW_LETTERS, random);
    }
  }

  return { width: GRID_SIZE, height: GRID_SIZE, grid, wordPositions };
};

// Places every word in an 8x8 grid and fills the rest with random letters; null when the words cannot all fit.
export const buildGrid = (words: string[], random: () => number = Math.random, attempts = 30): WordSearchGrid | null => {
  if (words.length === 0) return null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    const grid = tryBuild(words, random);
    if (grid) return grid;
  }
  return null;
};
